import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mic, MicOff, Volume2, VolumeX, ExternalLink, AlertTriangle, 
  Shield, Skull, Terminal, Cpu, Wifi, Database, Lock, Eye,
  Play, Pause, X, ChevronRight, FileText, Brain, Zap, Radio
} from "lucide-react";

// ============== PROJECTS DATA ==============
const PROJECTS = [
  {
    id: "dailyglobe",
    name: "DailyGlobe School",
    description: "Plateforme d'entraînement journalistique avec IA - Quiz quotidiens, grammaire, interviews, SEO",
    keywords: ["journalisme", "école", "reporter", "quiz", "formation journaliste", "presse", "dailyglobe"],
    url: "https://dailyglobe.school",
    deployed: true,
    icon: "📰",
    credentials: {
      type: "login",
      email: "demo@dailyglobe.school",
      password: "test123"
    }
  },
  {
    id: "scoutmedia",
    name: "ScoutMediaAI",
    description: "Veille médiatique IA - Monitoring de marque en temps réel, analyse de sentiment, alertes intelligentes",
    keywords: ["veille", "média", "scout", "monitoring", "presse", "sentiment", "marque", "brand", "news"],
    url: "https://www.scoutmediaai.com",
    deployed: true,
    icon: "📡",
    credentials: {
      type: "vip",
      email: "jbgiraud@mac.com",
      instruction: "Cliquez sur Accès VIP puis entrez votre email"
    }
  },
  {
    id: "achat-immo",
    name: "Achat Immo Gagnant",
    description: "Simulateur de rentabilité locative avec génération de dossier bancaire PDF - Analyse Airbnb vs location traditionnelle",
    keywords: ["immobilier", "investissement", "locatif", "rentabilité", "banque", "airbnb", "location", "immo"],
    url: "https://www.achat-immo-gagnant.fr",
    deployed: true,
    icon: "🏠",
    credentials: {
      type: "vip",
      email: "jbgiraud@mac.com",
      instruction: "Cliquez sur Accès VIP puis entrez votre email"
    }
  },
  {
    id: "hal3000",
    name: "HAL 3000 - Smart Cockpit",
    description: "Système domotique IA avec interface cyberpunk - Contrôle maison intelligente style vaisseau spatial",
    keywords: ["domotique", "maison", "hal", "smart home", "cockpit", "alexa", "iot", "maison intelligente", "hal3000"],
    url: "https://hal3000.fr",
    deployed: true,
    icon: "🤖"
  },
  {
    id: "auto-edito",
    name: "Auto-Edito",
    description: "Générateur de contenus éditoriaux IA - Création automatique d'articles et de posts optimisés SEO",
    keywords: ["edito", "contenu", "article", "seo", "rédaction", "auto", "génération", "texte"],
    url: "https://auto-edito.com",
    deployed: true,
    icon: "✍️",
    credentials: {
      type: "login",
      email: "jbgiraud@mac.com",
      password: "Gek90e99"
    }
  },
  {
    id: "cyber-lily",
    name: "Cyber Lily - Voice Recovery",
    description: "Application de clonage vocal IA - Communiquer avec sa propre voix clonée par intelligence artificielle",
    keywords: ["voix", "vocal", "clone", "lily", "parler", "communication", "handicap", "voice"],
    url: "https://cyber-lily.preview.emergentagent.com",
    deployed: true,
    icon: "🎤"
  },
  {
    id: "galile-rh",
    name: "Galilé RH",
    description: "Recrutement innovant par évaluation de personnalité IA - 37 PME, 1100 collaborateurs, Groupe Galilé",
    keywords: ["galilé", "rh", "recrutement", "emploi", "industrie", "personnalité", "carrière", "job"],
    url: null,
    deployed: false,
    icon: "👥"
  },
  {
    id: "super-senateur",
    name: "Super Sénateur",
    description: "Assistant IA pour campagne électorale - Sénatoriales 2026 - Propulsé par Grok IA",
    keywords: ["sénateur", "élection", "campagne", "politique", "sénatoriales", "commune", "vote"],
    url: "https://www.super-senateur.fr/dashboard",
    deployed: true,
    icon: "🏛️"
  },
  {
    id: "vibe-coding",
    name: "Vibe Coding - Global Intelligence",
    description: "Site satirique de démo créé en 1h30 - Démonstration de création ultra-rapide avec l'IA",
    keywords: ["vibe", "coding", "satire", "démo", "global", "intelligence", "rapide", "ia"],
    url: "https://www.global-intelligence.fr",
    deployed: true,
    icon: "🎭"
  }
];

// ============== SOUND EFFECTS ==============
const playSound = (type) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    switch(type) {
      case 'boot':
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.3);
        break;
      case 'beep':
        // Classic computer BIP sound - more audible
        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.08);
        break;
      case 'alert':
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.4);
        break;
      case 'success':
        oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.4);
        break;
      case 'hack':
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(100, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(50, audioContext.currentTime + 0.5);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.5);
        break;
      case 'error':
        // Windows-style error sound - louder and more dramatic
        oscillator.type = 'square';
        oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(200, audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.6, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.35);
        break;
      case 'click':
        // UI click feedback
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.03);
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.03);
        break;
      default:
        break;
    }
  } catch (e) {
    console.log('Audio not supported');
  }
};

// ============== FAKE POWERPOINT INTRO ==============
const FakePowerPoint = ({ onRageClick }) => {
  const [showSpider, setShowSpider] = useState(false);
  const [showWeb, setShowWeb] = useState(false);
  const [showError, setShowError] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [spiderPos, setSpiderPos] = useState({ x: -100, y: 50 });
  const [spiderAngle, setSpiderAngle] = useState(0);
  const clickTimerRef = useRef(null);
  const spiderIntervalRef = useRef(null);

  // Show spider after 10 seconds - walking animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpider(true);
      
      // Animate spider walking across screen - SLOWER and more visible
      let step = 0;
      const totalSteps = 80; // More steps = slower
      const startX = -150;
      const endX = window.innerWidth * 0.7; // Don't go all the way across
      const startY = 150;
      const endY = window.innerHeight / 2;
      
      spiderIntervalRef.current = setInterval(() => {
        step++;
        const progress = step / totalSteps;
        
        // Diagonal path from top-left to center
        const x = startX + (endX - startX) * progress;
        const y = startY + (endY - startY) * progress;
        
        // Wobble like walking
        const wobbleX = Math.sin(step * 0.4) * 8;
        const wobbleY = Math.sin(step * 0.6) * 6;
        
        // Slight rotation wobble
        const angle = Math.sin(step * 0.3) * 8;
        
        setSpiderPos({ x: x + wobbleX, y: y + wobbleY });
        setSpiderAngle(angle);
        
        // Start web when spider reaches center area
        if (step === Math.floor(totalSteps * 0.5)) {
          setShowWeb(true);
        }
        
        if (step >= totalSteps) {
          clearInterval(spiderIntervalRef.current);
        }
      }, 100); // 100ms per step = slower
    }, 10000);
    
    return () => {
      clearTimeout(timer);
      if (spiderIntervalRef.current) clearInterval(spiderIntervalRef.current);
    };
  }, []);

  const handleClick = () => {
    // Always play a click sound
    playSound('click');
    
    // Show error on first click
    if (!showError) {
      setTimeout(() => playSound('error'), 100);
      setShowError(true);
      return;
    }

    // Count rage clicks
    setClickCount(prev => {
      const newCount = prev + 1;
      setTimeout(() => playSound('error'), 50);
      
      // Reset click count after 2 seconds of no clicking
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => setClickCount(0), 2000);
      
      // After 5+ rapid clicks, launch the real experience
      if (newCount >= 5) {
        onRageClick();
      }
      
      return newCount;
    });
  };

  return (
    <div 
      className="fixed inset-0 z-[100] cursor-pointer select-none overflow-hidden"
      onClick={handleClick}
      style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
      }}
    >
      {/* Fake PowerPoint UI */}
      <div className="absolute inset-0 flex flex-col">
        {/* Fake ribbon/toolbar */}
        <div className="bg-[#2b579a] h-28 flex flex-col">
          {/* Title bar */}
          <div className="h-8 bg-[#1e3a5f] flex items-center px-4 text-white/80 text-sm">
            <span>Microsoft PowerPoint - Présentation1.pptx</span>
            <div className="ml-auto flex gap-2">
              <div className="w-4 h-4 border border-white/40" />
              <div className="w-4 h-4 border border-white/40" />
              <div className="w-4 h-4 bg-red-600" />
            </div>
          </div>
          {/* Ribbon tabs */}
          <div className="flex gap-1 px-2 pt-1 text-white text-xs">
            <div className="px-3 py-1 bg-[#2b579a]">Fichier</div>
            <div className="px-3 py-1 bg-[#4472c4] rounded-t">Accueil</div>
            <div className="px-3 py-1">Insertion</div>
            <div className="px-3 py-1">Création</div>
            <div className="px-3 py-1">Transitions</div>
            <div className="px-3 py-1">Animations</div>
            <div className="px-3 py-1">Diaporama</div>
          </div>
          {/* Ribbon content */}
          <div className="flex-1 bg-[#f3f3f3] flex items-center px-4 gap-4">
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 bg-gray-300 rounded" />
              <span className="text-[10px] text-gray-600">Coller</span>
            </div>
            <div className="h-12 w-px bg-gray-300" />
            <div className="flex gap-2">
              <div className="w-6 h-6 bg-gray-200 rounded text-xs flex items-center justify-center font-bold">G</div>
              <div className="w-6 h-6 bg-gray-200 rounded text-xs flex items-center justify-center italic">I</div>
              <div className="w-6 h-6 bg-gray-200 rounded text-xs flex items-center justify-center underline">S</div>
            </div>
          </div>
        </div>

        {/* Slide area */}
        <div className="flex-1 flex">
          {/* Slide panel */}
          <div className="w-48 bg-[#f3f3f3] p-2 border-r border-gray-300">
            <div className="bg-white border-2 border-blue-500 p-2 aspect-[4/3] flex items-center justify-center">
              <span className="text-[8px] text-gray-400">Diapositive 1</span>
            </div>
          </div>

          {/* Main slide */}
          <div className="flex-1 bg-[#e0e0e0] p-8 flex items-center justify-center relative">
            <div 
              className="bg-white shadow-xl w-full max-w-4xl aspect-[16/9] p-12 flex flex-col relative"
              style={{ fontFamily: 'Times New Roman, serif' }}
            >
              {/* Deliberately bad formatting */}
              <div className="flex-1 flex flex-col justify-center">
                <h1 
                  className="text-4xl md:text-5xl text-gray-800 leading-tight"
                  style={{ 
                    textAlign: 'left',
                    marginLeft: '15px',
                    fontFamily: 'Times New Roman, serif',
                    fontWeight: 'normal'
                  }}
                >
                  Ce qu'il faut savoir de
                </h1>
                <h1 
                  className="text-4xl md:text-5xl text-gray-800 leading-tight mt-2"
                  style={{ 
                    textAlign: 'left',
                    marginLeft: '45px',
                    fontFamily: 'Times New Roman, serif',
                    fontWeight: 'normal'
                  }}
                >
                  l'intelligence artificielle
                </h1>
                <h1 
                  className="text-4xl md:text-5xl text-gray-800 leading-tight mt-2"
                  style={{ 
                    textAlign: 'left',
                    marginLeft: '30px',
                    fontFamily: 'Times New Roman, serif',
                    fontWeight: 'normal'
                  }}
                >
                  en 2026
                </h1>
                
                <p className="text-gray-500 mt-12 text-lg" style={{ marginLeft: '60px' }}>
                  Cliquez pour continuer...
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status bar */}
        <div className="h-6 bg-[#f3f3f3] border-t border-gray-300 flex items-center px-4 text-xs text-gray-600">
          <span>Diapositive 1 sur 1</span>
          <span className="ml-auto">Français (France)</span>
        </div>
      </div>

      {/* BIG Spider Animation - Walking across screen */}
      {showSpider && (
        <div
          className="fixed z-[110] pointer-events-none transition-none"
          style={{ 
            left: spiderPos.x,
            top: spiderPos.y,
            transform: `rotate(${spiderAngle}deg)`,
            filter: 'drop-shadow(4px 4px 8px rgba(0,0,0,0.5))',
            fontSize: '120px',
            lineHeight: 1
          }}
        >
          🕷️
        </div>
      )}

      {/* Spider Web - Building progressively */}
      <AnimatePresence>
        {showWeb && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            className="fixed inset-0 z-[105] pointer-events-none flex items-center justify-center"
          >
            <svg width="800" height="800" viewBox="0 0 800 800" className="opacity-70">
              {/* Spider web pattern - white threads */}
              <g stroke="white" strokeWidth="2" fill="none">
                {/* Radial lines from center */}
                {[...Array(16)].map((_, i) => (
                  <motion.line 
                    key={`radial-${i}`}
                    x1="400" y1="400" 
                    x2={400 + 380 * Math.cos(i * Math.PI / 8)} 
                    y2={400 + 380 * Math.sin(i * Math.PI / 8)}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.8 }}
                    transition={{ duration: 1, delay: i * 0.1 }}
                  />
                ))}
                {/* Concentric spiral circles */}
                {[60, 120, 180, 240, 300, 360].map((r, i) => (
                  <motion.circle 
                    key={`circle-${i}`} 
                    cx="400" cy="400" r={r}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.6 }}
                    transition={{ duration: 1.5, delay: 1.5 + i * 0.2 }}
                  />
                ))}
              </g>
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Microsoft Error Dialog */}
      <AnimatePresence>
        {showError && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/30"
          >
            <div className="bg-[#f0f0f0] border border-gray-400 shadow-xl rounded-sm w-[450px]">
              {/* Dialog title bar */}
              <div className="bg-gradient-to-r from-[#0078d4] to-[#106ebe] px-3 py-2 flex items-center justify-between">
                <span className="text-white text-sm font-semibold">Microsoft Office</span>
                <button className="text-white hover:bg-red-500 w-6 h-6 flex items-center justify-center text-lg">
                  ×
                </button>
              </div>
              
              {/* Dialog content */}
              <div className="p-6 flex gap-4">
                <div className="text-4xl">⚠️</div>
                <div className="flex-1">
                  <p className="text-gray-800 font-semibold mb-2">
                    Votre licence Microsoft Office 365 n'est pas à jour
                  </p>
                  <p className="text-gray-600 text-sm">
                    Veuillez vous reconnecter pour continuer à utiliser PowerPoint.
                  </p>
                </div>
              </div>
              
              {/* Dialog buttons */}
              <div className="px-6 pb-4 flex justify-end gap-2">
                <button className="px-4 py-1.5 bg-[#0078d4] text-white text-sm rounded hover:bg-[#106ebe]">
                  Se reconnecter
                </button>
                <button className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded border border-gray-300 hover:bg-gray-300">
                  Annuler
                </button>
              </div>
            </div>

            {/* Click counter hint */}
            {clickCount >= 2 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 text-sm"
              >
                {clickCount < 5 ? `Encore ${5 - clickCount} clics...` : 'Lancement...'}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============== EXPLOSION CINEMATIC - HOLLYWOOD STYLE ==============
const ExplosionCinematic = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);

  // Initialize audio context immediately
  useEffect(() => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
    } catch (e) {
      console.log('Audio init error:', e);
    }
  }, []);

  // MASSIVE explosion sound - LOUDER
  const playMassiveExplosion = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      
      // Resume if suspended (Safari fix)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Layer 1: Deep boom - LOUDER
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(80, ctx.currentTime);
      osc1.frequency.exponentialRampToValueAtTime(20, ctx.currentTime + 1.2);
      gain1.gain.setValueAtTime(1, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.2);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start();
      osc1.stop(ctx.currentTime + 1.2);

      // Layer 2: Noise burst - LOUDER
      const bufferSize = ctx.sampleRate * 1;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 0.3);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(1, ctx.currentTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(3000, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 1);
      noise.connect(filter);
      filter.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noise.start();

      // Layer 3: Electric crackle - LOUDER
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sawtooth';
        osc2.frequency.setValueAtTime(150, ctx.currentTime);
        osc2.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.4);
        gain2.gain.setValueAtTime(0.6, ctx.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start();
        osc2.stop(ctx.currentTime + 0.4);
      }, 100);
    } catch (e) {
      console.log('Audio error:', e);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    let particles = [];
    let cracks = [];
    let flashes = [];
    let animationId;
    let startTime = Date.now();

    // Create explosion particles
    const createExplosion = (x, y, intensity) => {
      playMassiveExplosion();
      for (let i = 0; i < intensity; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 5 + Math.random() * 15;
        particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 5 + Math.random() * 20,
          life: 1,
          color: `hsl(${20 + Math.random() * 30}, 100%, ${50 + Math.random() * 30}%)`,
          type: 'fire'
        });
      }
      // Add sparks
      for (let i = 0; i < intensity / 2; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 10 + Math.random() * 20;
        particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 2 + Math.random() * 4,
          life: 1,
          color: '#fff',
          type: 'spark'
        });
      }
      // Screen flash
      flashes.push({ intensity: 1 });
    };

    // Create crack
    const createCrack = (startX, startY) => {
      const points = [{ x: startX, y: startY }];
      let x = startX, y = startY;
      const segments = 10 + Math.floor(Math.random() * 15);
      for (let i = 0; i < segments; i++) {
        const angle = Math.random() * Math.PI - Math.PI / 2;
        const length = 20 + Math.random() * 60;
        x += Math.cos(angle) * length;
        y += Math.sin(angle) * length + 20;
        points.push({ x, y });
        // Branch
        if (Math.random() > 0.6) {
          let bx = x, by = y;
          const branchPoints = [{ x: bx, y: by }];
          for (let j = 0; j < 5; j++) {
            const bAngle = Math.random() * Math.PI - Math.PI / 2;
            bx += Math.cos(bAngle) * 30;
            by += Math.sin(bAngle) * 15 + 10;
            branchPoints.push({ x: bx, y: by });
          }
          cracks.push({ points: branchPoints, alpha: 0, growing: true });
        }
      }
      cracks.push({ points, alpha: 0, growing: true });
    };

    // Animation loop
    const animate = () => {
      const elapsed = Date.now() - startTime;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Screen shake
      if (elapsed < 4000 && phase >= 1) {
        const shakeX = (Math.random() - 0.5) * 30;
        const shakeY = (Math.random() - 0.5) * 30;
        ctx.save();
        ctx.translate(shakeX, shakeY);
      }

      // Draw and update particles
      particles = particles.filter(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.3; // gravity
        p.life -= 0.02;
        p.size *= 0.98;

        if (p.life > 0) {
          ctx.globalAlpha = p.life;
          if (p.type === 'fire') {
            const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
            gradient.addColorStop(0, '#fff');
            gradient.addColorStop(0.3, p.color);
            gradient.addColorStop(1, 'transparent');
            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillStyle = p.color;
            ctx.fillRect(p.x, p.y, p.size, p.size);
          }
          return true;
        }
        return false;
      });

      // Draw cracks (electric blue zigzags)
      cracks.forEach(crack => {
        if (crack.growing && crack.alpha < 1) {
          crack.alpha += 0.1;
        }
        ctx.globalAlpha = crack.alpha;
        ctx.strokeStyle = '#00ffff';
        ctx.lineWidth = 3;
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 20;
        ctx.beginPath();
        crack.points.forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();
        // White core
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      // Draw flashes
      flashes = flashes.filter(f => {
        f.intensity -= 0.05;
        if (f.intensity > 0) {
          ctx.globalAlpha = f.intensity;
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          return true;
        }
        return false;
      });

      ctx.globalAlpha = 1;
      if (elapsed < 4000 && phase >= 1) {
        ctx.restore();
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    // Timeline of explosions
    // Phase 1: First explosions
    setTimeout(() => {
      setPhase(1);
      createExplosion(canvas.width * 0.3, canvas.height * 0.4, 100);
      createCrack(canvas.width * 0.2, 0);
    }, 500);

    setTimeout(() => {
      createExplosion(canvas.width * 0.7, canvas.height * 0.3, 80);
      createCrack(canvas.width * 0.8, 0);
    }, 900);

    setTimeout(() => {
      createExplosion(canvas.width * 0.5, canvas.height * 0.5, 120);
      createCrack(canvas.width * 0.5, 0);
      createCrack(canvas.width * 0.4, 0);
    }, 1300);

    // Phase 2: More explosions
    setTimeout(() => {
      setPhase(2);
      createExplosion(canvas.width * 0.2, canvas.height * 0.6, 90);
      createExplosion(canvas.width * 0.8, canvas.height * 0.5, 90);
    }, 1800);

    setTimeout(() => {
      createExplosion(canvas.width * 0.4, canvas.height * 0.3, 100);
      createExplosion(canvas.width * 0.6, canvas.height * 0.7, 100);
    }, 2300);

    // Phase 3: Final massive explosion
    setTimeout(() => {
      setPhase(3);
      createExplosion(canvas.width * 0.5, canvas.height * 0.5, 200);
      for (let i = 0; i < 8; i++) {
        createCrack(Math.random() * canvas.width, 0);
      }
    }, 3000);

    // Blackout and complete
    setTimeout(() => {
      setPhase(4);
    }, 4000);

    setTimeout(() => {
      cancelAnimationFrame(animationId);
      onComplete();
    }, 4500);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-[100] bg-black overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0" />
      
      {/* Scan lines overlay */}
      <div 
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
        }}
      />

      {/* Final blackout */}
      {phase >= 4 && (
        <motion.div
          className="absolute inset-0 bg-black z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      )}
    </div>
  );
};

// ============== CINEMATIC INTRO ==============
const CinematicIntro = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  const [bootLines, setBootLines] = useState([]);
  const [progress, setProgress] = useState(0);

  const bootSequence = [
    { text: "[BIOS] Initialisation système neuronal...", delay: 200 },
    { text: "[BOOT] Chargement noyau quantique v7.2.1...", delay: 300 },
    { text: "[MEM] Allocation mémoire holographique: 128TB...", delay: 200 },
    { text: "[NET] Connexion réseau neuronal établie...", delay: 250 },
    { text: "[CRYPTO] Activation chiffrement AES-512...", delay: 300 },
    { text: "[AI] Réveil intelligence artificielle LEA...", delay: 400 },
    { text: "[SCAN] Analyse biométrique utilisateur...", delay: 350 },
    { text: "[AUTH] Identification: JEAN-BAPTISTE GIRAUD", delay: 300 },
    { text: "[ACCESS] Niveau d'accès: COMMANDANT SUPRÊME", delay: 250 },
    { text: "[FIREWALL] Boucliers quantiques activés...", delay: 200 },
    { text: "[DEFENSE] Système anti-intrusion opérationnel...", delay: 300 },
    { text: "[READY] Tous systèmes opérationnels.", delay: 400 },
  ];

  useEffect(() => {
    // Phase 0: Initial blackout with glitch
    const timer1 = setTimeout(() => {
      playSound('boot');
      setPhase(1);
    }, 500);

    return () => clearTimeout(timer1);
  }, []);

  useEffect(() => {
    if (phase === 1) {
      // Boot sequence
      let lineIndex = 0;
      const addLine = () => {
        if (lineIndex < bootSequence.length) {
          playSound('beep');
          const currentLine = bootSequence[lineIndex];
          setBootLines(prev => [...prev, currentLine.text]);
          setProgress(((lineIndex + 1) / bootSequence.length) * 100);
          const delay = currentLine.delay || 200;
          lineIndex++;
          setTimeout(addLine, delay);
        } else {
          setTimeout(() => {
            playSound('success');
            setPhase(2);
          }, 500);
        }
      };
      setTimeout(addLine, 800);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === 2) {
      // Final reveal
      setTimeout(() => {
        onComplete();
      }, 2000);
    }
  }, [phase, onComplete]);

  return (
    <motion.div 
      className="fixed inset-0 z-[100] bg-black overflow-hidden"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      {/* Scan lines effect */}
      <div className="absolute inset-0 pointer-events-none z-50">
        <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(0,255,255,0.03)_50%)] bg-[length:100%_4px]" />
      </div>

      {/* Glitch overlay */}
      <motion.div 
        className="absolute inset-0 bg-cyan-500/5"
        animate={{ 
          opacity: [0, 0.1, 0, 0.05, 0],
          x: [0, -5, 5, -2, 0]
        }}
        transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 2 }}
      />

      {/* Boot terminal */}
      <AnimatePresence>
        {phase >= 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center p-8"
          >
            <div className="w-full max-w-4xl">
              {/* Header */}
              <motion.div 
                className="flex items-center gap-4 mb-6 border-b border-cyan-500/30 pb-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Terminal className="w-8 h-8 text-cyan-400" />
                <div>
                  <h1 className="text-cyan-400 text-2xl font-mono font-bold tracking-wider">
                    LEA COMMAND CENTER v7.2
                  </h1>
                  <p className="text-cyan-400/60 text-sm font-mono">
                    SYSTÈME NEURAL QUANTIQUE | ACCÈS RESTREINT
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                </div>
              </motion.div>

              {/* Boot lines */}
              <div className="font-mono text-sm space-y-1 mb-8 h-64 overflow-hidden">
                {bootLines.map((line, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex items-center gap-2 ${
                      line.includes('[READY]') ? 'text-green-400' :
                      line.includes('[AUTH]') || line.includes('[ACCESS]') ? 'text-yellow-400' :
                      line.includes('[AI]') ? 'text-purple-400' :
                      'text-cyan-400/80'
                    }`}
                  >
                    <span className="text-green-500">✓</span>
                    {line}
                  </motion.div>
                ))}
                <motion.span 
                  className="inline-block w-3 h-5 bg-cyan-400"
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                />
              </div>

              {/* Progress bar */}
              <div className="relative">
                <div className="h-2 bg-cyan-900/30 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs font-mono text-cyan-400/60">
                  <span>INITIALISATION SYSTÈME</span>
                  <span>{Math.round(progress)}%</span>
                </div>
              </div>

              {/* Phase 2: Welcome message */}
              {phase === 2 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-12 text-center"
                >
                  <motion.div
                    className="text-6xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
                    animate={{ 
                      textShadow: [
                        "0 0 20px rgba(0,255,255,0.5)",
                        "0 0 40px rgba(0,255,255,0.8)",
                        "0 0 20px rgba(0,255,255,0.5)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    BIENVENUE, COMMANDANT
                  </motion.div>
                  <p className="text-cyan-400/60 mt-4 text-xl font-mono">
                    Accès au Centre de Commande autorisé
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Corner decorations */}
      <div className="absolute top-4 left-4 w-20 h-20 border-l-2 border-t-2 border-cyan-500/30" />
      <div className="absolute top-4 right-4 w-20 h-20 border-r-2 border-t-2 border-cyan-500/30" />
      <div className="absolute bottom-4 left-4 w-20 h-20 border-l-2 border-b-2 border-cyan-500/30" />
      <div className="absolute bottom-4 right-4 w-20 h-20 border-r-2 border-b-2 border-cyan-500/30" />
    </motion.div>
  );
};

// ============== HACKING ALERT COMPONENT ==============
const HackingAlert = ({ attacker, onDefeat }) => {
  const [phase, setPhase] = useState('detecting'); // detecting, fighting, defeated
  const [defenseProgress, setDefenseProgress] = useState(0);

  useEffect(() => {
    playSound('hack');
    
    // Phase 1: Detection
    setTimeout(() => {
      setPhase('fighting');
      playSound('alert');
    }, 2000);

    // Phase 2: Fighting back
    const interval = setInterval(() => {
      setDefenseProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setPhase('defeated');
          playSound('success');
          setTimeout(onDefeat, 3000);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [onDefeat]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center"
    >
      {/* Red alert flashing */}
      <motion.div
        className="absolute inset-0 bg-red-500/20"
        animate={{ opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 0.5, repeat: Infinity }}
      />

      <div className="relative z-10 text-center max-w-2xl p-8">
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
        >
          {phase === 'detecting' && (
            <>
              <AlertTriangle className="w-24 h-24 text-red-500 mx-auto mb-4" />
              <h2 className="text-4xl font-bold text-red-500 font-mono mb-4">
                ⚠️ INTRUSION DÉTECTÉE ⚠️
              </h2>
              <p className="text-red-400 text-xl font-mono">
                Tentative de piratage par: <span className="text-yellow-400">{attacker}</span>
              </p>
            </>
          )}

          {phase === 'fighting' && (
            <>
              <Shield className="w-24 h-24 text-cyan-500 mx-auto mb-4 animate-pulse" />
              <h2 className="text-4xl font-bold text-cyan-400 font-mono mb-4">
                🛡️ CONTRE-MESURES ACTIVÉES
              </h2>
              <div className="w-full h-4 bg-gray-800 rounded-full overflow-hidden mb-4">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-500 to-green-500"
                  style={{ width: `${defenseProgress}%` }}
                />
              </div>
              <p className="text-cyan-400 font-mono">
                Neutralisation de l'attaque: {defenseProgress}%
              </p>
            </>
          )}

          {phase === 'defeated' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <Skull className="w-24 h-24 text-green-500 mx-auto mb-4" />
              </motion.div>
              <h2 className="text-4xl font-bold text-green-400 font-mono mb-4">
                ✅ ATTAQUE NEUTRALISÉE
              </h2>
              <p className="text-green-400 text-xl font-mono mb-4">
                "{attacker}" ajouté à la liste des <span className="text-red-500 font-bold">LOOSERS</span>
              </p>
              <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 mt-4">
                <p className="text-red-400 font-mono text-sm">
                  📋 LOOSERS LIST: {attacker} • Script Kiddie #42 • Hacker Wannabe
                </p>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

// ============== MAIN COMMAND CENTER ==============
const CommandCenterPage = () => {
  const [showFakePPT, setShowFakePPT] = useState(true);
  const [showExplosion, setShowExplosion] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [leaMessage, setLeaMessage] = useState("");
  const [userInput, setUserInput] = useState("");
  const [showHacking, setShowHacking] = useState(false);
  const [currentAttacker, setCurrentAttacker] = useState("");
  const [searchingProject, setSearchingProject] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  const ATTACKERS = [
    "Brown Fintube France Energy",
    "CyberCorp Industries",
    "DarkNet Syndicate",
    "Quantum Hackers Ltd",
    "Shadow Systems Inc"
  ];

  // Handle rage click from fake PowerPoint -> Start EXPLOSION
  const handleRageClick = () => {
    setShowFakePPT(false);
    setShowExplosion(true);
  };

  // After explosion -> Start boot sequence
  const handleExplosionComplete = () => {
    setShowExplosion(false);
    setShowIntro(true);
  };

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true; // Show interim results
      recognitionRef.current.lang = 'fr-FR';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setUserInput(transcript);
        
        // Only process final results
        if (event.results[0].isFinal) {
          handleUserCommand(transcript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          speakLea("Je n'ai pas accès au microphone. Veuillez autoriser l'accès.");
        } else if (event.error === 'no-speech') {
          speakLea("Je n'ai pas entendu. Réessayez en parlant plus fort.");
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    } else {
      console.log('Speech recognition not supported');
    }
  }, []);

  // Random hacking events - only after main interface is shown
  useEffect(() => {
    if (showFakePPT || showExplosion || showIntro) return;
    
    const scheduleHack = () => {
      const delay = 180000 + Math.random() * 60000; // 3-4 minutes
      return setTimeout(() => {
        const attacker = ATTACKERS[Math.floor(Math.random() * ATTACKERS.length)];
        setCurrentAttacker(attacker);
        setShowHacking(true);
        scheduleHack();
      }, delay);
    };

    const hackTimer = scheduleHack();
    return () => clearTimeout(hackTimer);
  }, [showFakePPT, showExplosion, showIntro]);

  // LEA greeting ONLY after cinematic intro completes (not during fake PPT or explosion)
  useEffect(() => {
    if (!showFakePPT && !showExplosion && !showIntro) {
      setTimeout(() => {
        speakLea("Bonjour Jean-Baptiste ! Qu'est-ce que je peux faire pour vous ?");
      }, 1000);
    }
  }, [showFakePPT, showExplosion, showIntro]);

  const speakLea = (text) => {
    setLeaMessage(text);
    setIsSpeaking(true);
    
    if (soundEnabled && synthRef.current) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.rate = 1;
      utterance.pitch = 1.1;
      
      // Try to get a female French voice
      const voices = synthRef.current.getVoices();
      const frenchFemale = voices.find(v => v.lang.includes('fr') && v.name.includes('Female')) ||
                          voices.find(v => v.lang.includes('fr'));
      if (frenchFemale) utterance.voice = frenchFemale;
      
      utterance.onend = () => setIsSpeaking(false);
      synthRef.current.speak(utterance);
    } else {
      setTimeout(() => setIsSpeaking(false), 3000);
    }

    setConversationHistory(prev => [...prev, { role: 'lea', text }]);
  };

  const startListening = () => {
    if (recognitionRef.current) {
      try {
        setIsListening(true);
        setUserInput("");
        recognitionRef.current.start();
        playSound('beep');
      } catch (e) {
        console.error('Error starting recognition:', e);
        setIsListening(false);
        speakLea("Erreur avec le microphone. Réessayez.");
      }
    } else {
      speakLea("La reconnaissance vocale n'est pas supportée par ce navigateur.");
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const findProject = (query) => {
    const normalizedQuery = query.toLowerCase();
    return PROJECTS.find(project => 
      project.keywords.some(keyword => normalizedQuery.includes(keyword)) ||
      project.name.toLowerCase().includes(normalizedQuery) ||
      normalizedQuery.includes(project.name.toLowerCase())
    );
  };

  const handleUserCommand = (command) => {
    setConversationHistory(prev => [...prev, { role: 'user', text: command }]);
    const lowerCommand = command.toLowerCase();

    // Check for project request
    if (lowerCommand.includes('montre') || lowerCommand.includes('affiche') || 
        lowerCommand.includes('projet') || lowerCommand.includes('ouvre')) {
      
      const project = findProject(lowerCommand);
      
      if (project) {
        setSearchingProject(project);
        speakLea(`Recherche en cours... J'ai trouvé ${project.name}. Ouverture du projet.`);
        
        setTimeout(() => {
          window.open(project.url, '_blank');
          setSearchingProject(null);
        }, 2500);
      } else {
        speakLea("Je n'ai pas trouvé ce projet. Pouvez-vous préciser ou le sélectionner manuellement ?");
      }
    } else if (lowerCommand.includes('aide') || lowerCommand.includes('help')) {
      speakLea("Vous pouvez me demander d'afficher vos projets. Par exemple: 'Montre-moi le projet immobilier' ou 'Affiche DailyGlobe'.");
    } else {
      speakLea("Je suis à votre écoute, Jean-Baptiste. Que souhaitez-vous faire ?");
    }
  };

  const [wakingServer, setWakingServer] = useState(null);

  // Wake up server function - makes real HTTP request
  const wakeUpServer = async (project, e) => {
    e.stopPropagation();
    if (!project.url) return;
    
    setWakingServer(project.id);
    playSound('beep');
    speakLea(`Réveil du serveur ${project.name} en cours...`);
    
    try {
      // Make a real fetch to wake up the server
      const response = await fetch(project.url, { 
        method: 'HEAD',
        mode: 'no-cors'
      });
      
      // Wait a bit for server to fully wake
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      speakLea(`Serveur ${project.name} réveillé ! Prêt à l'emploi.`);
    } catch (error) {
      // Even if fetch fails due to CORS, the server will wake up
      await new Promise(resolve => setTimeout(resolve, 2000));
      speakLea(`Signal envoyé au serveur ${project.name}. Réveil en cours.`);
    } finally {
      setWakingServer(null);
    }
  };

  const openProject = (project) => {
    playSound('beep');
    
    if (!project.url) {
      speakLea(`Le projet ${project.name} n'a pas encore d'URL configurée.`);
      return;
    }
    
    if (!project.deployed) {
      speakLea(`Le projet ${project.name} est en cours de développement. Serveurs en veille.`);
      return;
    }
    
    speakLea(`Ouverture de ${project.name}...`);
    
    // Open immediately with correct URL
    const url = project.url.startsWith('http') ? project.url : `https://${project.url}`;
    console.log('Opening URL:', url);
    
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      // Popup blocked - try alternative
      speakLea(`Popup bloqué. Cliquez à nouveau ou autorisez les popups.`);
      window.location.href = url;
    }
  };

  // Phase 1: Fake PowerPoint
  if (showFakePPT) {
    return <FakePowerPoint onRageClick={handleRageClick} />;
  }

  // Phase 2: EXPLOSION CINEMATIC
  if (showExplosion) {
    return <ExplosionCinematic onComplete={handleExplosionComplete} />;
  }

  // Phase 3: Boot sequence intro
  if (showIntro) {
    return <CinematicIntro onComplete={() => setShowIntro(false)} />;
  }

  // Phase 4: Main command center
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-hidden relative">
      {/* Animated background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-500 rounded-full"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0.3
            }}
            animate={{ 
              y: [null, Math.random() * -500],
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{ 
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* Hacking alert overlay */}
      <AnimatePresence>
        {showHacking && (
          <HackingAlert 
            attacker={currentAttacker} 
            onDefeat={() => setShowHacking(false)} 
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="relative z-10 border-b border-cyan-500/20 bg-black/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            >
              <Cpu className="w-10 h-10 text-cyan-400" />
            </motion.div>
            <div>
              <h1 className="text-2xl font-bold text-cyan-400 font-mono">
                LEA COMMAND CENTER
              </h1>
              <div className="flex items-center gap-4 text-xs text-cyan-400/60 font-mono">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  SYSTÈME ACTIF
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  SÉCURISÉ
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5 text-cyan-400" /> : <VolumeX className="w-5 h-5 text-cyan-400/50" />}
            </button>
            <div className="text-right text-xs font-mono text-cyan-400/60">
              <div>COMMANDANT: J-B GIRAUD</div>
              <div>SESSION: {new Date().toLocaleTimeString('fr-FR')}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* LEA AI Interface */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-br from-cyan-900/20 to-purple-900/20 border border-cyan-500/30 rounded-2xl p-6 backdrop-blur-xl">
            <div className="flex items-start gap-6">
              {/* LEA Avatar - Female Android Image */}
              <motion.div
                className="w-24 h-24 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-cyan-500/50"
                animate={isSpeaking ? { scale: [1, 1.05, 1], borderColor: ['rgba(0,255,255,0.5)', 'rgba(0,255,255,1)', 'rgba(0,255,255,0.5)'] } : {}}
                transition={{ duration: 0.5, repeat: isSpeaking ? Infinity : 0 }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png"
                  alt="LEA - Assistant IA"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* LEA Message */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-cyan-400">LEA</h2>
                  <span className="text-xs px-2 py-0.5 bg-cyan-500/20 rounded-full text-cyan-400">
                    Assistant IA v7.2
                  </span>
                  {isSpeaking && (
                    <motion.div
                      className="flex items-center gap-1"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <Radio className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-400">En train de parler...</span>
                    </motion.div>
                  )}
                </div>
                
                <p className="text-white text-lg mb-4 min-h-[3rem]">
                  {leaMessage || "Initialisation..."}
                </p>

                {/* Voice control + Text input fallback */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <motion.button
                      onClick={isListening ? stopListening : startListening}
                      className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                        isListening 
                          ? 'bg-red-500 text-white' 
                          : 'bg-cyan-500 text-black hover:bg-cyan-400'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isListening ? (
                        <>
                          <MicOff className="w-5 h-5" />
                          Arrêter
                        </>
                      ) : (
                        <>
                          <Mic className="w-5 h-5" />
                          Parler à LEA
                        </>
                      )}
                    </motion.button>

                    {isListening && (
                      <motion.div
                        className="flex items-center gap-2 text-red-400"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 0.5, repeat: Infinity }}
                      >
                        <span className="w-3 h-3 rounded-full bg-red-500" />
                        Écoute en cours...
                      </motion.div>
                    )}

                    {userInput && (
                      <span className="text-cyan-400/60 text-sm">
                        Vous: "{userInput}"
                      </span>
                    )}
                  </div>
                  
                  {/* Text input fallback */}
                  <form 
                    onSubmit={(e) => {
                      e.preventDefault();
                      const input = e.target.elements.command.value;
                      if (input.trim()) {
                        setUserInput(input);
                        handleUserCommand(input);
                        e.target.elements.command.value = '';
                      }
                    }}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      name="command"
                      placeholder="Ou tapez votre commande ici... (ex: montre-moi le projet immobilier)"
                      className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-cyan-500/30 text-white placeholder-white/30 focus:outline-none focus:border-cyan-500"
                    />
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-xl bg-purple-500 text-white font-semibold hover:bg-purple-400 transition-colors"
                    >
                      Envoyer
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Searching animation */}
            <AnimatePresence>
              {searchingProject && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/30"
                >
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Cpu className="w-6 h-6 text-cyan-400" />
                    </motion.div>
                    <span className="text-cyan-400 font-mono">
                      Recherche: {searchingProject.name}...
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Projects Grid */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5" />
            PROJETS DÉPLOYÉS
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROJECTS.map((project, idx) => (
              <motion.a
                key={project.id}
                href={project.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={(e) => {
                  if (!project.url || !project.deployed) {
                    e.preventDefault();
                    if (!project.url) {
                      speakLea(`Le projet ${project.name} est en cours de développement.`);
                    } else {
                      speakLea(`Le projet ${project.name} n'est pas encore en ligne.`);
                    }
                    return;
                  }
                  playSound('beep');
                  
                  // Announce credentials if they exist
                  if (project.credentials) {
                    if (project.credentials.type === 'vip') {
                      speakLea(`Ouverture de ${project.name}. Pour vous connecter, cliquez sur Accès VIP, puis entrez votre email: ${project.credentials.email.replace('@', ' arobase ').replace('.', ' point ')}`);
                    } else if (project.credentials.type === 'login') {
                      speakLea(`Ouverture de ${project.name}. Identifiants de connexion: email ${project.credentials.email.replace('@', ' arobase ').replace('.', ' point ')}, mot de passe: ${project.credentials.password}`);
                    }
                  } else {
                    speakLea(`Ouverture de ${project.name}...`);
                  }
                }}
                className={`group relative bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-cyan-500/20 hover:border-cyan-500/50 rounded-xl p-4 text-left transition-all hover:scale-[1.02] block ${project.url && project.deployed ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
              >
                {/* Status indicator */}
                <div className="absolute top-3 right-3 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${project.deployed ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`} />
                  <span className="text-xs font-mono text-cyan-400/60">
                    {project.deployed ? 'ONLINE' : 'STANDBY'}
                  </span>
                </div>

                <div className="text-3xl mb-2">{project.icon}</div>
                <h4 className="text-white font-bold mb-1 group-hover:text-cyan-400 transition-colors">
                  {project.name}
                </h4>
                <p className="text-gray-400 text-sm mb-2 line-clamp-2">
                  {project.description}
                </p>
                
                {/* Credentials display */}
                {project.credentials && (
                  <div className="bg-black/40 rounded-lg p-2 mb-2 border border-cyan-500/10">
                    {project.credentials.type === 'vip' ? (
                      <div className="text-xs">
                        <span className="text-yellow-400 font-bold">🔑 Accès VIP</span>
                        <p className="text-gray-400 mt-1">{project.credentials.email}</p>
                      </div>
                    ) : (
                      <div className="text-xs space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-cyan-400/60">Email:</span>
                          <span className="text-white font-mono">{project.credentials.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-cyan-400/60">Pass:</span>
                          <span className="text-white font-mono">{project.credentials.password}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-cyan-400 text-sm">
                  <ExternalLink className="w-4 h-4" />
                  <span>{project.deployed && project.url ? 'Ouvrir le projet' : 'En développement'}</span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Status bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-xl border-t border-cyan-500/20 py-2 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-xs font-mono text-cyan-400/60">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-1">
                <Wifi className="w-3 h-3 text-green-500" />
                RÉSEAU: OPTIMAL
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3 text-green-500" />
                CHIFFREMENT: AES-512
              </span>
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3 text-green-500" />
                SURVEILLANCE: ACTIVE
              </span>
            </div>
            <div>
              LEA COMMAND CENTER © 2026 | Conçu par Jean-Baptiste Giraud
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CommandCenterPage;
